import React, { useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Box, CircularProgress, Collapse, IconButton, Pagination, TableSortLabel, Typography, lighten, useTheme } from '@mui/material';
import get from 'lodash/get';

import { ReactComponent as AscendingSortingIcon } from '../assets/icons/main/ascending_sorting.svg';
import { ReactComponent as DescendingIcon } from '../assets/icons/main/descending_sorting.svg';
import { ReactComponent as NoSortIcon } from '../assets/icons/main/no-sort.svg';


import { useOutletContext } from 'react-router-dom';
import { palette } from '../theme/palette';
import { ReactComponent as KeyboardArrowDownIcon } from '../assets/icons/main/expand-down-icon.svg';
import { ReactComponent as KeyboardArrowUpIcon } from '../assets/icons/main/collapse-up-icon.svg';

const ROW_HEIGHT = 67;
const TABLE_HEIGHT = ROW_HEIGHT * 5;

const Styles = {
  paper: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: '0px 0px 18px 18px',
    boxShadow: 'none',
    position: 'relative',
  },
  loadingWrapper: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  root: {
    '.MuiTableCell-root': {
      fontSize: '1rem',
      fontStyle: 'normal',
      fontWeight: 400,
      whiteSpace: 'nowrap',
    },
  },
  sortable: { '& .MuiTableSortLabel-icon': { opacity: 0.5, width: '1rem' } },
  cellText: {
    fontSize: '1rem',
    fontStyle: 'normal',
    fontWeight: '400',

  },
  paginationWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '76px',
  },
};

// function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
//   if (b[orderBy] < a[orderBy]) {
//     return -1;
//   }
//   if (b[orderBy] > a[orderBy]) {
//     return 1;
//   }
//   return 0;
// }

// function getComparator<Key extends keyof any>(
//   order: Order,
//   orderBy: Key
// ): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
//   return order === 'desc'
//     ? (a, b) => descendingComparator(a, b, orderBy)
//     : (a, b) => -descendingComparator(a, b, orderBy);
// }

// function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
//   const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
//   stabilizedThis.sort((a, b) => {
//     const order = comparator(a[0], b[0]);
//     if (order !== 0) {
//       return order;
//     }
//     return a[1] - b[1];
//   });
//   return stabilizedThis.map((el) => el[0]);
// }

type Order = 'asc' | 'desc';

interface EnhancedHeadProps {
  headCells: any[];
  filterRow: any[];
  orderBy: string;
  order: Order;
  onRequestSort: (property: string) => void;
  isExpandable?: boolean;
  isFromBulkAssetsPopup?: boolean;
  headerHeight?: any;
}

const EnhancedHead = ({ headCells, filterRow, orderBy, order, onRequestSort, isExpandable, isFromBulkAssetsPopup, headerHeight }: EnhancedHeadProps) => {
  const themeData: any = useTheme();
  const themeMode: any = get(useOutletContext(), 'themeMode', {});

  const createSortHandler = (property: string) => (e: React.MouseEvent<unknown>) => {
    onRequestSort(property);
  };

  const getIcon = (isActive: boolean) => {
    if (!isActive) return NoSortIcon;
    return DescendingIcon;
  };

  return (
    <TableHead sx={{ backgroundColor: palette.primary.contrastText }}>
      <TableRow sx={{ height: isFromBulkAssetsPopup ? '76px' : headerHeight || ROW_HEIGHT }}>
        {headCells.map((headCell, idx) => (
          <TableCell
            key={headCell.field}
            style={{
              minWidth: headCell.minWidth || '', width: headCell.width || '', maxWidth: headCell.maxWidth || '',
              backgroundColor: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05),
              borderBottom: `1px solid ${themeData.palette.customBorderColor.main}`,
              // padding: idx === headCells.length - 1 ? '0px' : '6px 24px',
              paddingLeft: idx === 0 ? '24px' : undefined,
              // paddingRight: idx === headCells.length - 1 ? '0px' : undefined,

            }}
            align={headCell.headerAlign || 'center'}
          >
            {headCell.sortable ? (
              <TableSortLabel
                active={orderBy === headCell.field}
                direction={orderBy === headCell.field ? order : 'asc'}
                IconComponent={getIcon(orderBy === headCell.field)}
                onClick={createSortHandler(headCell.field)}
                sx={Styles.sortable}
              >
                <Typography
                  component='span'
                  sx={{
                    textTransform: 'uppercase',
                    color: themeData.palette.text.primary,
                    fontSize: '1rem',
                    fontStyle: 'normal',
                    fontWeight: 600,
                  }}
                >
                  {headCell.headerName}
                </Typography>
              </TableSortLabel>
            ) : (
              <Typography
                component='span'
                sx={{
                  textTransform: 'uppercase',
                  color: themeData.palette.text.primary,
                  fontSize: '1rem',
                  fontStyle: 'normal',
                  fontWeight: 600,
                }}
              >
                {headCell.headerName}
              </Typography>
            )}
          </TableCell>
        ))}
      </TableRow>
      {filterRow.length ? (
        <TableRow sx={{ height: ROW_HEIGHT }}>
          {filterRow.map((cell) => (
            <TableCell key={cell.id} style={{
              top: ROW_HEIGHT,
              backgroundColor: (themeMode.isLightBG) ? lighten(themeData.palette.background.default, 0.9) : lighten(themeData.palette.background.default, 0.05),
              borderBottom: `1px solid ${themeData.palette.customBorderColor.main}`
            }}>
              {cell.renderCell ? cell.renderCell(cell) : ''}
            </TableCell>
          ))}
        </TableRow>
      ) : (
        <></>
      )}
    </TableHead>
  );
};

const EmptyRow = ({ children, compHeight, colSpan }: any) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ border: 'none' }}>
      <Box
        sx={{
          height: `${compHeight - 146}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>
    </TableCell>
  </TableRow>
);

const CustomDataTable = ({
  rows,
  columns,
  filterRow = [],
  tableHeight,
  rowHeight = ROW_HEIGHT,
  rowsPerPage = 10,
  loading,
  isServerPagination,
  totalRowCount,
  onPagination,
  page = 0,
  onSorting = () => { },
  isExpandable,
  noRowsLabel,
  expandedRows,
  accordianComponent,
  getRowId,
  getRowSx,
  isFromBulkAssetsPopup,
  headerHeight
}: any) => {
  const themeData: any = get(useOutletContext(), 'themeData', {});
  const themeMode: any = get(useOutletContext(), 'themeMode', {});
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState<Order>('asc');
  // const [page, setPage] = useState(0);
  const [noDataHeight, setNoDataHeight] = useState(0);

  const tableContainerRef = useRef(null);

  useEffect(() => {
    setNoDataHeight(get(tableContainerRef, 'current.clientHeight', 0));
  }, [tableContainerRef]);

  // useLayoutEffect(() => {
  //   setPage(0);
  // }, [rows.length]);

  const handleRequestSort = (property: string) => {
    const field = orderBy === property && order === 'desc' ? '' : property;
    const sort = orderBy === property && order === 'asc' ? 'desc' : 'asc';
    setOrder(sort);
    setOrderBy(field);
    onSorting({ field, sort: field ? sort : '' }, 'SORT');
  };

  useEffect(() => {
    setOrder('asc');
    setOrderBy('');
  }, [columns.length]);

  const handleChangePage = (event: unknown, newPage: number) => {
    // setPage(newPage - 1);

    if (isServerPagination) {
      onPagination(rowsPerPage, newPage);
    }
  };

  // const visibleRows = React.useMemo(
  //   () => stableSort(rows, getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
  //   [order, orderBy, page, rows, rowsPerPage]
  // );
  // const visibleRows = React.useMemo(() => stableSort(rows, getComparator(order, orderBy)), [order, orderBy, rows]);

  // const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const startIndex = (page - 1) * rowsPerPage + 1;
  const endIndex = Math.min(page * rowsPerPage, totalRowCount);

  const [tableExpand, setTableExpand] = useState<any>({ 1: false });
  const handleExpandState = (row: any, index: any) => {
    setTableExpand({ [row.id]: !tableExpand[row.id] })
  }

  const safeRowsPerPage = Math.max(1, Number(rowsPerPage) || 10);
  const safeTotalRowCount = Math.max(0, Number(totalRowCount) || 0);
  const shouldShowPagination = safeTotalRowCount > safeRowsPerPage;


  return (
    <Paper sx={Styles.paper}>
      {loading && (
        <Box
          sx={{
            ...Styles.loadingWrapper,
            backgroundColor: themeMode.isLightBG
              ? lighten(themeData.palette.background.default, 0.9)
              : lighten(themeData.palette.background.default, 0.05),
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <TableContainer
        ref={tableContainerRef}
        sx={{
          minHeight: tableHeight || TABLE_HEIGHT,
          backgroundColor: themeMode.isLightBG
            ? lighten(themeData.palette.background.default, 0.9)
            : lighten(themeData.palette.background.default, 0.05),
        }}
      >
        <Table stickyHeader aria-label='sticky table' sx={{ minWidth: 650 }} size='small'>
          <EnhancedHead
            headCells={columns}
            filterRow={filterRow}
            orderBy={orderBy}
            order={order}
            onRequestSort={handleRequestSort}
            isExpandable={isExpandable}
            isFromBulkAssetsPopup={isFromBulkAssetsPopup}
            headerHeight={headerHeight}
          />
          <TableBody>
            {rows.flatMap((row: any, index: number) => {
              const resolvedRowId = (getRowId && getRowId(row, index)) || row.id || row.name || index;
              const rowKey = String(resolvedRowId);
              const customRowSx = typeof getRowSx === 'function' ? getRowSx(row, index) || {} : {};
              const customCellSx = get(customRowSx, '.MuiTableCell-root', {});
              const { ['.MuiTableCell-root']: _customCellRoot, ...customRowRootSx } = customRowSx;
              const defaultCellBg = themeMode.isLightBG
                ? (index + 1) % 2 == 0
                  ? lighten(themeData.palette.background.default, 0.9)
                  : lighten(themeData.palette.background.default, 0.2)
                : (index + 1) % 2 == 0
                  ? lighten(themeData.palette.background.default, 0.05)
                  : lighten(themeData.palette.background.default, 0.08);
              const isExpanded = Boolean(isExpandable && expandedRows?.includes(resolvedRowId));

              const renderedRows = [
                <TableRow
                  key={`row-${rowKey}`}
                  sx={{
                    height: rowHeight,
                    ...Styles.root,
                    ...customRowRootSx,
                    '.MuiTableCell-root': {
                      ...Styles.root['.MuiTableCell-root'],
                      paddingTop: '6px',
                      paddingBottom: isFromBulkAssetsPopup ? '0px' : '6px',
                      borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${themeData.palette.customBorderColor.main}`,
                      //  backgroundColor: ((index + 1) % 2 == 0) ? lighten(themeData.palette.background.default, 0.05) : lighten(themeData.palette.background.default, 0.08),// for lighter theme 0.9 and 0.2
                      backgroundColor: defaultCellBg,
                      ...customCellSx,
                    },
                  }}
                >
                  {columns.map(
                    (column: {
                      field: string | number;
                      renderCell: any;
                      align?: 'center' | 'left' | 'right' | 'inherit' | 'justify' | undefined;
                      width?: number | string;
                      minWidth?: number | string;
                      maxWidth?: number | string;
                    }, colIdx: any) => (
                      <TableCell
                        key={column.field}
                        align={column.align || 'center'}
                        sx={{
                          ...Styles.root, minWidth: column.minWidth || '', width: column.width || '', maxWidth: column.maxWidth || '',
                          paddingLeft: colIdx === 0 ? '24px' : undefined,
                          paddingRight: colIdx === columns.length - 1 ? '24px !important' : undefined,
                        }}
                      >
                        {column.renderCell ? (
                          column.renderCell({ row, value: row[column.field] })
                        ) : (
                          <Box sx={{ width: column.width, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                            <Typography component='span' sx={Styles.cellText}>
                              {row[column.field]}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                    )
                  )}
                </TableRow>,
              ];

              if (isExpanded) {
                renderedRows.push(
                  <TableRow key={`expanded-row-${rowKey}`}>
                    <TableCell key={`expanded-cell-${rowKey}`} style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: isFromBulkAssetsPopup ? '12px' : '16px' }} colSpan={columns.length}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: isFromBulkAssetsPopup ? '0 0px 16px 0px' : '0 16px 32px 16px' }}>
                          {row.accordianComponent ? row.accordianComponent : accordianComponent}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                );
              }

              return renderedRows;
            })}

            {!loading && !rows.length && (
              <EmptyRow compHeight={noDataHeight} colSpan={columns.length}>
                <span style={{ fontSize: '1rem' }}>{noRowsLabel}</span>
              </EmptyRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {shouldShowPagination && (
        <Box
          sx={{
            ...Styles.paginationWrapper,
            borderTop: `1px solid ${themeData.palette.customBorderColor.main}`,
            // backgroundColor: lighten(themeData.palette.background.default, 0.2)//for lighter theme
            backgroundColor: themeMode.isLightBG
              ? lighten(themeData.palette.background.default, 0.2)
              : lighten(themeData.palette.background.default, 0.05),
            height: isFromBulkAssetsPopup ? '66px' : '76px',
          }}
        >
          <Box>
            <Typography sx={{ padding: '12px', fontSize: '0.9rem', fontWeight: 400 }}>
              Showing {startIndex}-{endIndex} of {totalRowCount} entries
            </Typography>
          </Box>
          <Box sx={{ padding: '12px' }}>
            <Pagination
              variant='outlined'
              shape='rounded'
              page={page}
              onChange={handleChangePage}
              count={isServerPagination ? Math.ceil(totalRowCount / rowsPerPage) : Math.ceil(rows.length / rowsPerPage)}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default CustomDataTable;
